import React, { useEffect, useRef, useState, useCallback, FC, useContext } from "react";
import { Button, message } from "antd";
import { CloseOutlined, MenuOutlined } from "src/icons";

import { JoyrideState } from "src/shared/types";
import { ManageMetricIcon } from "src/assets/manage-metric.js";
import { useSlipper, useRedux } from "src/shared/hooks";
import { Layout, Container } from "./styles";
import {
  fetchManageMetrics,
  updateManageMetrics,
  clearManageMetrics,
  UpdateMetricsActionPayload,
  ClearTypes,
  IManageMetricsRootState,
} from "src/store/manageMetric";
import { rearrangeRowsinList, safeGet } from "src/shared/utils";
import { IAllManageMetrics, ISingleManageMetrics } from "src/shared/models/ManageMetricsModel";
import { IRootState } from "shared/types";
import { IActionFactory } from "src/store";
import colors from "src/styles/variables.scss";
import { JoyrideContext, IJoyride } from "src/core/App/useJoyride";

interface IManageMetricsProps {
  refreshHome: () => void;
  switchBackToHome: (val: boolean) => void;
}

type MappedStateProps = {
  metrics: IManageMetricsRootState;
};

type MappedActionProps = {
  fetchManageMetrics();
  updateManageMetrics(payload: UpdateMetricsActionPayload);
  clearManageMetrics(payload?: ClearTypes);
};

const ManageMetrics: FC<IManageMetricsProps> = (props) => {
  const { showJoyrideMap, cancelJoyride } = useContext(JoyrideContext) as IJoyride;
  const mappedProps = useRedux<IRootState, MappedStateProps, MappedActionProps>(
    (state) => ({
      metrics: state.home.pinnedMetrics.manageMetrics,
    }),
    {
      fetchManageMetrics,
      updateManageMetrics,
      clearManageMetrics,
    }
  );

  const updatedList = useRef<IAllManageMetrics>({
    allmetrics: [],
    pageNo: 0,
    pageSize: 0,
    hasMore: false,
    formatedFilterData: [],
  });

  const updateFlag = useRef(false);
  const [metricsList, setMetricsList] = useState<ISingleManageMetrics[]>([]);

  //custom function provided to the useslipper
  const handleSlipperChange = useCallback(
    (initialIndex: number, finalIndex: number) => {
      const updatedArr = rearrangeRowsinList([...updatedList.current.allmetrics], initialIndex, finalIndex);
      const updatechangedList = {
        ...updatedList.current,
        allmetrics: updatedArr,
      };
      setMetricsList(rearrangeRowsinList(metricsList, initialIndex, finalIndex));

      updatedList.current = updatechangedList;
      updateFlag.current = true;
    },
    [metricsList, updateFlag]
  );

  //update Submit handler
  const onUpdateSubmitMetrics = useCallback((): void => {
    //update only if the changes was made to the updated list else just navigate back to home
    if (updateFlag.current) {
      mappedProps.updateManageMetrics({
        data: [...safeGet(updatedList.current, "allmetrics", [])],
        switchBackToHome: () => {
          props.switchBackToHome(false);
          props.refreshHome();
        },
      });
    } else {
      props.switchBackToHome(false);
    }
    mappedProps.clearManageMetrics();
  }, []);

  //deleting the row
  const onDeleteRow = useCallback(
    (evt, rowID): void => {
      try {
        const modifiedFilterData = [...updatedList.current.formatedFilterData];
        const metricList = metricsList.filter((curr, index) => {
          if (curr.id === rowID) {
            modifiedFilterData.splice(index, 1);
          }
          return curr.id !== rowID;
        });

        setMetricsList(metricList);
        const newMetricList = {
          ...updatedList.current,
          allmetrics: metricList,
          formatedFilterData: modifiedFilterData,
        };
        updatedList.current = newMetricList;

        //set update flag to true
        updateFlag.current = true;
      } catch (e) {
        message.error("Error Deleting Metrices.TRY AGAIN");
      }
      evt.stopPropagation();
    },
    [metricsList, updatedList.current, updateFlag.current]
  );

  const metricsRef = useSlipper({
    handleSelector: "slider",
    onChange: handleSlipperChange,
  });

  //calling the API to fetch notes -componentDidMount
  useEffect(() => {
    //clearing the reducer
    mappedProps.clearManageMetrics();
    //fetching the API
    mappedProps.fetchManageMetrics();
  }, []);
  const cancelTutor = useCallback(() => {
    cancelJoyride(JoyrideState.MANAGE_METRICS);
  }, [JoyrideState.MANAGE_METRICS]);
  //setting the notesList ARRAY after fetching data
  useEffect(() => {
    const { payload } = mappedProps.metrics.fetchedManageMetrics;
    if (payload) {
      setMetricsList(payload.allmetrics);
      updatedList.current = payload;
    }
  }, [mappedProps.metrics.fetchedManageMetrics]);

  return (
    <Layout>
      <Container>
        <div className="header">
          <div className="headerRow">
            <Button
              className="close-btn"
              icon={<CloseOutlined className="text-lg" />}
              onClick={() => props.switchBackToHome(false)}
            ></Button>
            <div className="headerTitle">
              <span>
                Manage Focus Metrics
                {showJoyrideMap[JoyrideState.MANAGE_METRICS] && (
                  <div className="manageMetricTooltipGroup">
                    <div className="arrowUP"></div>
                    <div className="manageMetricTooltip">
                      <div className="manageMetricTooltipText">re-order or remove pinned metrices</div>
                      <CloseOutlined className="manageMetricTooltipClose" onClick={cancelTutor} />
                    </div>
                  </div>
                )}
              </span>
            </div>
            <Button className="save-btn" onClick={onUpdateSubmitMetrics}>
              SAVE
            </Button>
          </div>
        </div>
        <div className="metricbody">
          <ul className="metricList" ref={metricsRef}>
            {metricsList &&
              metricsList.map((curr, index) => {
                return (
                  <li className="eachMetric" key={curr.id + curr.label} id={`${index}`}>
                    <div className="parallelRow">
                      <div className="eachMetricRow">
                        <div className="cross" onClick={(evt) => onDeleteRow(evt, curr.id)}>
                          <CloseOutlined style={{ color: colors.milkyWhite }} />
                        </div>
                        <div className="metric-info">
                          <span className="metricTitle">{curr.label} </span>
                          <span className="metricStore">{updatedList.current.formatedFilterData[index]}</span>
                        </div>
                        <div className="slider">
                          <MenuOutlined style={{ color: colors.milkyWhite }} />
                        </div>
                      </div>
                      <div className="eachNoteSeparator" />
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      </Container>
    </Layout>
  );
};
export default ManageMetrics;
